// ContactPage.js
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  FiMail, FiPhone, FiMapPin, FiClock, FiSend,
  FiZap, FiShield, FiSmile, FiNavigation, FiArrowRight,
} from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import useSiteSettings from '../hooks/useSiteSettings';
import './OtherPages.css';

/* ── Fallback contact info (mirrors backend DEFAULTS) ─────────── */
const DEFAULT_CONTACT = {
  primaryAddress: 'Angel Printers, L-8, Bisanpura, Sector 58, Noida, Uttar Pradesh - 201301',
  branchAddress:  'Angel Printers (B2B), D-125, Sector 10, Noida, Uttar Pradesh - 201301',
  phone:  '+91 8585949411',
  email:  'info@angelprinters.com',
  hours:  'Mon – Sat: 10am – 7pm IST',
};

/* ── Fallback hero image (workspace photo from Unsplash) ──────── */
const FALLBACK_HERO_IMG =
  'https://images.unsplash.com/photo-1497366216548-37526070297c?w=900&q=80';

/* ── Google Maps embed for Angel Printers Noida ──────────────── */
const MAP_EMBED_SRC =
  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.7793088034655!2d77.35333027528806!3d28.606396775679045!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce52e320dabeb%3A0x9e320ccfded33cad!2sANGEL%20PRINTERS!5e0!3m2!1sen!2sin!4v1789214714304!5m2!1sen!2sin';
const MAP_DIRECTIONS_URL =
  'https://maps.google.com/?q=Angel+Printers+L-8+Bisanpura+Sector+58+Noida+Uttar+Pradesh';

export const ContactPage = () => {
  /* ── Form state ─────────────────────────────────────────────── */
  const EMPTY = { name: '', email: '', phone: '', subject: '', message: '', type: 'contact' };
  const [form, setForm]       = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  /* ── Settings from Admin ─────────────────────────────────────── */
  const { data: contactSettings } = useSiteSettings('contact');
  const { data: heroSettings }    = useSiteSettings('contact_hero');
  const c        = contactSettings || DEFAULT_CONTACT;
  const heroImg  = heroSettings?.heroImage || FALLBACK_HERO_IMG;

  /* ── Handlers ────────────────────────────────────────────────── */
  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handlePhone = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm(f => ({ ...f, phone: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.phone)) {
      toast.error('Please enter a valid 10-digit Indian mobile number.');
      return;
    }
    setSending(true);
    try {
      await API.post('/contact', form);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setSubmitted(true);
      setForm(EMPTY);
    } catch {
      toast.error('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  /* ── Contact info cards ──────────────────────────────────────── */
  const infoCards = [
    {
      icon: <FiPhone size={20} />,
      label: 'Phone',
      lines: [c.phone],
      href: `tel:${(c.phone || '').replace(/\s/g, '')}`,
    },
    {
      icon: <FiMail size={20} />,
      label: 'Email',
      lines: [c.email],
      href: `mailto:${c.email}`,
    },
    {
      icon: <FiMapPin size={20} />,
      label: 'Our Location',
      lines: [c.primaryAddress],
    },
    {
      icon: <FiClock size={20} />,
      label: 'Business Hours',
      lines: [c.hours, 'Sunday: 11:00 AM – 4:00 PM'],
    },
  ];

  /* ── Hero badges ────────────────────────────────────────────── */
  const badges = [
    { icon: <FiZap size={16} />,    title: 'Quick Response',  sub: 'We reply within 24 hours' },
    { icon: <FiShield size={16} />, title: 'Support',         sub: 'Before & After Order' },
    { icon: <FiSmile size={16} />,  title: 'Friendly Team',   sub: 'Here to help' },
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us | Angel Printers</title>
        <meta name="description" content="Get in touch with Angel Printers. Call, email, or fill out the form for custom printing quotes, support, and general enquiries." />
      </Helmet>

      <div className="cp-page">

        {/* ══════════════════════════════════════════════════════
            HERO
        ══════════════════════════════════════════════════════ */}
        <section className="cp-hero">
          <div className="container cp-hero-inner">

            {/* Left: text */}
            <div className="cp-hero-text">
              <span className="cp-eyebrow">CONTACT US</span>
              <h1 className="cp-hero-heading">
                We're Here to <span className="cp-accent">Help</span>
              </h1>
              <p className="cp-hero-desc">
                Have a question, need a custom quote, or just want to say hello?
                Our team is always ready to assist you. Reach out to us —
                we'd love to hear from you.
              </p>
              <div className="cp-badges">
                {badges.map((b) => (
                  <div key={b.title} className="cp-badge">
                    <span className="cp-badge-icon">{b.icon}</span>
                    <div>
                      <div className="cp-badge-title">{b.title}</div>
                      <div className="cp-badge-sub">{b.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: image */}
            <div className="cp-hero-img-wrap">
              <div className="cp-hero-img-deco" aria-hidden="true" />
              <img
                src={heroImg}
                alt="Angel Printers workspace"
                className="cp-hero-img"
                onError={(e) => { e.target.src = FALLBACK_HERO_IMG; }}
              />
            </div>

          </div>
        </section>

        {/* ══════════════════════════════════════════════════════
            GET IN TOUCH + FORM
        ══════════════════════════════════════════════════════ */}
        <section className="cp-section">
          <div className="container">
            <div className="cp-contact-grid">

              {/* Left: info */}
              <div className="cp-info-panel">
                <h2 className="cp-section-title">Get in Touch</h2>
                <p className="cp-info-desc">
                  Fill out the form and we'll get back to you as soon as possible.
                  For urgent enquiries, feel free to call or WhatsApp us.
                </p>
                <div className="cp-info-cards">
                  {infoCards.map((card) => (
                    <div key={card.label} className="cp-info-card">
                      <span className="cp-info-icon" aria-hidden="true">{card.icon}</span>
                      <div className="cp-info-content">
                        <div className="cp-info-label">{card.label}</div>
                        {card.lines.map((line, i) =>
                          card.href && i === 0 ? (
                            <a key={i} href={card.href} className="cp-info-val cp-info-link">{line}</a>
                          ) : (
                            <div key={i} className="cp-info-val">{line}</div>
                          )
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: form */}
              <div className="cp-form-panel">
                <h2 className="cp-section-title">Send Us a Message</h2>

                {submitted ? (
                  <div className="cp-success">
                    <div className="cp-success-icon">✓</div>
                    <h3>Message Sent!</h3>
                    <p>Thanks for reaching out. We'll get back to you within 24 hours.</p>
                    <button
                      className="btn btn-outline"
                      onClick={() => setSubmitted(false)}
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate>

                    {/* Row 1: Name + Phone */}
                    <div className="cp-form-row">
                      <div className="form-group">
                        <label htmlFor="cp-name">
                          Name <span className="cp-required" aria-hidden="true">*</span>
                        </label>
                        <input
                          id="cp-name"
                          className="form-control"
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          required
                          autoComplete="name"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="cp-phone">
                          Phone <span className="cp-required" aria-hidden="true">*</span>
                        </label>
                        <input
                          id="cp-phone"
                          className="form-control"
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={handlePhone}
                          placeholder="Your phone number"
                          maxLength={10}
                          required
                          autoComplete="tel"
                          inputMode="numeric"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="form-group">
                      <label htmlFor="cp-email">
                        Email <span className="cp-required" aria-hidden="true">*</span>
                      </label>
                      <input
                        id="cp-email"
                        className="form-control"
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        required
                        autoComplete="email"
                      />
                    </div>

                    {/* Subject (maps to backend 'type' + 'subject') */}
                    <div className="form-group">
                      <label htmlFor="cp-subject">Subject</label>
                      <select
                        id="cp-subject"
                        className="form-control"
                        name="type"
                        value={form.type}
                        onChange={handleChange}
                      >
                        <option value="contact">General Enquiry</option>
                        <option value="quote">Request a Quote</option>
                        <option value="support">Order Support</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div className="form-group">
                      <label htmlFor="cp-message">
                        Message <span className="cp-required" aria-hidden="true">*</span>
                      </label>
                      <textarea
                        id="cp-message"
                        className="form-control"
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        rows={5}
                        placeholder="How can we help you?"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary cp-submit-btn"
                      disabled={sending}
                    >
                      {sending ? (
                        <span className="cp-spinner" aria-hidden="true" />
                      ) : (
                        <FiSend size={16} aria-hidden="true" />
                      )}
                      {sending ? 'Sending…' : 'Send Message'}
                    </button>

                  </form>
                )}
              </div>

            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════
            MAP / LOCATION
        ══════════════════════════════════════════════════════ */}
        <section className="cp-map-section">
          <div className="container">
            <div className="cp-map-grid">

              {/* Map embed */}
              <div className="cp-map-wrap">
                <iframe
                  title="Angel Printers Location"
                  src={MAP_EMBED_SRC}
                  className="cp-map-iframe"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>

              {/* Location info */}
              <div className="cp-map-info">
                <h2 className="cp-section-title">Find Us Here</h2>
                <p className="cp-map-desc">
                  Visit our office for a quick discussion or to see our print samples.
                </p>
                <div className="cp-map-address">
                  <FiMapPin size={18} className="cp-map-pin" aria-hidden="true" />
                  <div>
                    <div className="cp-map-biz">Angel Printers</div>
                    <div className="cp-map-addr-text">{c.primaryAddress}</div>
                  </div>
                </div>
                {/* <a
                  href={MAP_DIRECTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline cp-directions-btn"
                >
                  <FiNavigation size={15} aria-hidden="true" />
                  Get Directions
                </a> */}
              </div>

            </div>
          </div>
        </section>



      </div>
    </>
  );
};

// ServicesPage.js
export const ServicesPage = () => (
  <div style={{paddingTop:72}}>
    <div className="op-hero" style={{background:'linear-gradient(135deg,var(--secondary),#2d1b4e)',padding:'80px 0 60px',color:'#fff',textAlign:'center'}}>
      <div className="container">
        <span style={{display:'inline-block',background:'rgba(232,64,64,0.2)',color:'var(--primary-light)',padding:'6px 16px',borderRadius:100,fontSize:'0.78rem',fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:16}}>WHAT WE DO</span>
        <h1 className="op-hero-title" style={{fontSize:'2.5rem',fontWeight:800,color:'#fff',marginBottom:12}}>Our Services</h1>
        <p style={{color:'rgba(255,255,255,0.7)',fontSize:'1.05rem',maxWidth:600,margin:'0 auto'}}>From concept to delivery — we provide end-to-end printing solutions for businesses and individuals.</p>
      </div>
    </div>
    <div className="container op-section" style={{padding:'72px 24px'}}>
      <div className="op-services-grid" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:28}}>
        {[
          {emoji:'🖨️',title:'Digital Printing',desc:'High-quality digital printing for short to medium runs. Perfect for flyers, brochures, business cards, and more. Vibrant colours on premium paper stocks.'},
          {emoji:'🖼️',title:'Large Format',desc:'Banners, posters, signage and display graphics printed at any size. UV-resistant inks for indoor and outdoor use.'},
          {emoji:'👕',title:'Apparel Printing',desc:'Custom t-shirts, hoodies, and branded clothing via screen print, DTG, or embroidery. Minimum order from just 1 piece.'},
          {emoji:'📦',title:'Packaging',desc:'Custom branded boxes, bags, tissue paper and labels. Make every unboxing experience unforgettable.'},
          {emoji:'✂️',title:'Die-Cutting',desc:'Custom shapes and cuts for labels, stickers, cards, and packaging. Any shape, any size.'},
          {emoji:'🎨',title:'Design Services',desc:'Our in-house designers can create or refine your artwork ready for print. Upload a brief and we\'ll handle the rest.'},
        ].map(s=>(
          <div key={s.title} className="op-service-card" style={{background:'#fff',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:32,transition:'all 0.2s'}}>
            <div style={{fontSize:'2.4rem',marginBottom:16}}>{s.emoji}</div>
            <h3 style={{marginBottom:10,fontSize:'1.1rem'}}>{s.title}</h3>
            <p style={{color:'var(--text-muted)',fontSize:'0.9rem',lineHeight:1.65}}>{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// AboutPage.js
export const AboutPage = () => (
  <div style={{paddingTop:72}}>
    <div className="op-hero" style={{background:'linear-gradient(135deg,var(--secondary),#2d1b4e)',padding:'80px 0 60px',color:'#fff',textAlign:'center'}}>
      <div className="container">
        <span style={{display:'inline-block',background:'rgba(232,64,64,0.2)',color:'var(--primary-light)',padding:'6px 16px',borderRadius:100,fontSize:'0.78rem',fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:16}}>OUR STORY</span>
        <h1 className="op-hero-title" style={{fontSize:'2.5rem',fontWeight:800,color:'#fff',marginBottom:12}}>About Angel Printers</h1>
        <p style={{color:'rgba(255,255,255,0.7)',fontSize:'1.05rem',maxWidth:640,margin:'0 auto'}}>We're a team of printing professionals dedicated to delivering exceptional print quality at competitive prices.</p>
      </div>
    </div>
    <div className="container op-section" style={{padding:'72px 24px'}}>
      <div className="op-about-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:64,alignItems:'center',marginBottom:72}}>
        <div>
          <span style={{display:'inline-block',background:'rgba(232,64,64,0.08)',color:'var(--primary)',padding:'6px 16px',borderRadius:100,fontSize:'0.78rem',fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:16}}>WHO WE ARE</span>
          <h2 style={{fontSize:'2.2rem',fontWeight:800,marginBottom:20}}>Premium Printing Since 2010</h2>
          <p style={{color:'var(--text-muted)',lineHeight:1.8,marginBottom:16}}>Angel Printers was founded with a simple mission: to make high-quality custom printing accessible to everyone. From solo entrepreneurs to large corporations, we deliver the same exceptional quality on every order.</p>
          <p style={{color:'var(--text-muted)',lineHeight:1.8}}>Our state-of-the-art facility in India is equipped with the latest printing technology, staffed by experienced professionals who take pride in every job they produce.</p>
        </div>
        <img src="https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600" alt="Printing facility" style={{borderRadius:'var(--radius-lg)',width:'100%',height:380,objectFit:'cover'}}/>
      </div>
      <div className="op-stats-grid" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:24,background:'var(--bg-soft)',borderRadius:'var(--radius-lg)',padding:40}}>
        {[['2010','Founded'],['50K+','Customers'],['412+','Products'],['99.4%','Satisfaction']].map(([v,l])=>(
          <div key={l} style={{textAlign:'center'}}>
            <div style={{fontSize:'2.4rem',fontWeight:800,color:'var(--primary)',fontFamily:'var(--font-display)',marginBottom:4}}>{v}</div>
            <div style={{color:'var(--text-muted)',fontSize:'0.9rem'}}>{l}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// NotFoundPage.js
export const NotFoundPage = () => (
  <div style={{paddingTop:120,textAlign:'center',padding:'120px 24px'}}>
    <div style={{fontSize:'6rem',fontWeight:900,color:'var(--border)',fontFamily:'var(--font-display)',lineHeight:1}}>404</div>
    <h2 style={{fontSize:'1.8rem',marginBottom:12}}>Page Not Found</h2>
    <p style={{color:'var(--text-muted)',marginBottom:28}}>The page you're looking for doesn't exist or has been moved.</p>
    <a href="/" className="btn btn-primary btn-lg">Go Home</a>
  </div>
);

export default ContactPage;